// "Trending channels" — horizontal rail on Home featuring the
// fastest-growing creators of the last 7 days. Powered by
// GET /api/channel/trending (channels.py), which ranks by new
// subscribers in the window. The goal is a viral discovery loop:
// every creator (not just the biggest) gets a shot at the spotlight
// any week they grow.
//
// Why a separate component vs reusing ChannelSuggestionsRail with a
// prop: the headline copy, badge, and call-to-action differ — and
// the conditional rendering would have made the suggestions rail
// noisier than the value of the abstraction. Keep them parallel.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fire, BellSimple, ArrowRight, TrendUp } from "@phosphor-icons/react";
import api from "../lib/api";
import { subscribeTo } from "../lib/channels";
import { useAuth } from "../context/AuthContext";

export default function ChannelTrendingRail() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    // limit=8 so the rail fills out without overcrowding; days=7 is
    // the window we frame the headline against ("this week").
    api.get("/channel/trending", { params: { limit: 8, days: 7 } })
      .then((r) => setChannels(r.data || []))
      .catch(() => setChannels([]));
  }, [user]);

  const handleSubscribe = async (cid) => {
    try {
      const updated = await subscribeTo(cid);
      setChannels((arr) => arr.map((c) => (c.user_id === cid ? updated : c)));
    } catch (_) {}
  };

  if (!channels.length) return null;

  return (
    <section className="mt-10 md:mt-14" data-testid="home-channel-trending">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-terracotta flex items-center gap-1.5">
            <Fire size={11} weight="fill" /> Trending this week
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-forest mt-1.5 leading-tight">
            Channels on the rise
          </h2>
        </div>
        <Link to="/foryou" className="text-xs text-terracotta hover:underline inline-flex items-center gap-1" data-testid="channel-trending-see-all">
          Explore all <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 md:mx-0 md:px-0"
        style={{ scrollbarWidth: "thin" }}
        data-testid="channel-trending-rail"
      >
        {channels.map((c) => (
          <div
            key={c.user_id}
            className="snap-start shrink-0 w-52 md:w-60 rounded-2xl bg-white border border-cream-300 overflow-hidden group hover:border-terracotta transition-all relative"
            data-testid={`channel-trending-${c.user_id}`}
          >
            <Link to={`/u/${c.user_id}`} className="block">
              <div className="aspect-[3/1] relative overflow-hidden bg-ink">
                {(() => {
                  const raw = c.avatar_url || "";
                  const avatar = raw && !raw.startsWith("http")
                    ? `${process.env.REACT_APP_BACKEND_URL}${raw}`
                    : raw;
                  const seed = Math.abs([...(c.name || "Trending")].reduce((a, ch) => a + ch.charCodeAt(0), 0));
                  const stockFace = `https://i.pravatar.cc/640?img=${(seed % 70) + 1}`;
                  const src = c.banner_url || avatar || stockFace;
                  return (
                    <img
                      src={src}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.swapped) {
                          e.currentTarget.dataset.swapped = "1";
                          e.currentTarget.src = stockFace;
                        }
                      }}
                    />
                  );
                })()}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase font-bold bg-terracotta text-cream px-1.5 py-0.5 rounded-full shadow">
                  <TrendUp size={9} weight="bold" /> Rising
                </span>
              </div>
            </Link>
            <div className="p-3">
              <Link to={`/u/${c.user_id}`} className="block">
                <p className="font-semibold text-ink truncate">{c.name}</p>
                {c.tagline && <p className="text-[11px] text-muted_ink line-clamp-2 mt-0.5">{c.tagline}</p>}
                <p className="text-[10px] text-muted_ink mt-1">{c.subscriber_count} subs · {c.video_count} videos</p>
              </Link>
              {user && !c.is_self && (
                <button
                  onClick={() => handleSubscribe(c.user_id)}
                  disabled={c.is_subscribed_by_me}
                  data-testid={`channel-trending-subscribe-${c.user_id}`}
                  className={`w-full mt-3 h-9 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${
                    c.is_subscribed_by_me ? "bg-cream-200 text-forest" : "bg-forest text-cream hover:bg-forest-600"
                  }`}
                >
                  <BellSimple size={12} weight="fill" />
                  {c.is_subscribed_by_me ? "Subscribed" : "Subscribe"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
